/**
 * @sazeo/sdk — Policy Documents hooks
 *
 * TanStack Query wrappers for the policies addon. All calls route through
 * the Edge Function at `/addons/policies/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PolicyDocument {
  id: string;
  tenant_id: string;
  title: string;
  category: 'hr' | 'it' | 'security' | 'conduct' | 'safety' | 'benefits' | 'general' | 'other';
  body: string | null;
  version: string;
  status: 'draft' | 'published' | 'archived';
  effective_date: string | null;
  requires_acknowledgement: boolean;
  created_by: string | null;
  published_by: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PolicyCreateInput {
  title: string;
  category?: PolicyDocument['category'];
  body?: string | null;
  version?: string;
  effective_date?: string | null;
  requires_acknowledgement?: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const policyKeys = {
  all: ['policies'] as const,
  list: (tenantId: string) => [...policyKeys.all, 'list', tenantId] as const,
  detail: (policyId: string) => [...policyKeys.all, 'detail', policyId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function usePolicies(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: policyKeys.list(tenantId),
    queryFn: () => apiFetch<PolicyDocument[]>(supabase, 'addons/policies/policies'),
    enabled: Boolean(tenantId),
  });
}

export function usePolicy(supabase: SupabaseClientLike, tenantId: string, policyId: string) {
  return useQuery({
    queryKey: policyKeys.detail(policyId),
    queryFn: () => apiFetch<PolicyDocument>(supabase, `addons/policies/policies/${policyId}`),
    enabled: Boolean(tenantId) && Boolean(policyId),
  });
}

export function useCreatePolicy(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PolicyCreateInput) =>
      apiFetch<PolicyDocument>(supabase, 'addons/policies/policies', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: policyKeys.list(tenantId) });
    },
  });
}

export function useUpdatePolicy(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: { id: string; patch: Partial<PolicyCreateInput> & { status?: PolicyDocument['status'] } }) =>
      apiFetch<PolicyDocument>(supabase, `addons/policies/policies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: policyKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: policyKeys.detail(id) });
    },
  });
}

export function usePublishPolicy(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<PolicyDocument>(supabase, `addons/policies/policies/${id}/publish`, {
        method: 'PATCH',
      }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: policyKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: policyKeys.detail(id) });
    },
  });
}

export function useAcknowledgePolicy(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (policyId: string) =>
      apiFetch<{ acknowledged: true }>(
        supabase,
        `addons/policies/policies/${policyId}/acknowledge`,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: policyKeys.list(tenantId) });
    },
  });
}

export function usePendingPolicies(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: [...policyKeys.list(tenantId), 'pending'],
    queryFn: async () => {
      const rows = await apiFetch<PolicyDocument[]>(supabase, 'addons/policies/policies');
      return rows.filter(
        (policy) => policy.status === 'published' && policy.requires_acknowledgement,
      );
    },
    enabled: Boolean(tenantId),
  });
}
